/**
 * Yellow banner that displays the regimen / procedure name plus the
 * doc-preview CTAs ("View Tx Plan", "View Order Document", and
 * "View Clinical Document"). Both PDF CTAs open the shared doc viewer with
 * the oasis toolbar.
 *
 * @module singleOrderForExternal/components/RegimenNameBanner
 */
import { useDispatch } from "react-redux";
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import { useFacilityCategory } from "../../../hooks/useOncoemrTasksEnabled";
import { setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../redux/store/store";
import { getRegimenLabel } from "../utils/regimenLabel";

interface RegimenNameBannerProps {
  regimenName: string | undefined;
  authOnFile: AuthOnFile | undefined;
  clinical_document_path: string | undefined;
  onOpenImageViewer: () => void;
}

/**
 * @param props - Regimen name, auth-on-file payload, clinical-document path, image-viewer opener
 * @returns The yellow regimen banner with optional Tx-Plan / Order-Document / Clinical-Document CTAs
 */
const RegimenNameBanner = ({
  regimenName,
  authOnFile,
  clinical_document_path,
  onOpenImageViewer,
}: RegimenNameBannerProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const facilityCategory = useFacilityCategory();
  const screenshots = authOnFile?.screenshots;

  const openOrderDocument = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.DOC_VIEWER_MODAL,
        metaData: {
          filePath: screenshots?.radiology_auth_pdf?.screenshot_path,
          title: "Order Document",
          hideFooter: true,
          useOasisToolbar: true,
        },
      }),
    );
  };

  const openClinicalDocument = () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.DOC_VIEWER_MODAL,
        metaData: {
          filePath: clinical_document_path,
          title: "Clinical Document",
          hideFooter: true,
          useOasisToolbar: true,
        },
      }),
    );
  };

  return (
    <div className="single-order-for-external--content--regimen-name mb-4 flex items-center justify-between rounded bg-[#FFFDE9] p-3 text-small font-bold">
      <div>
        {getRegimenLabel(facilityCategory)} :{" "}
        <span className="font-normal">{regimenName ?? ""}</span>
      </div>
      <div className="auth-on-file-modals-cta flex items-center gap-2">
        {screenshots?.treatment_plan_path && (
          <span
            className="view-tx-plan--btn text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer"
            onClick={onOpenImageViewer}
          >
            View Tx Plan
          </span>
        )}
        {screenshots?.radiology_auth_pdf && (
          <span
            className="text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer"
            onClick={openOrderDocument}
          >
            View Order Document
          </span>
        )}
        {clinical_document_path && (
          <span
            className="view-clinical-document--btn text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer"
            onClick={openClinicalDocument}
          >
            View Clinical Document
          </span>
        )}
      </div>
    </div>
  );
};

export default RegimenNameBanner;
